import { HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TasksFilter } from '../../../../aumico-lib-cpmm-fe/src/lib/interfaces/tasks-filter.interface';
import { MfaFilter } from '../interfaces/mfa-filter.interface';
import { UsersConfig, UsersConfigService } from './../users.config';

@Injectable({
    providedIn: 'root',
})
export class RoutesUtil {
    private readonly changePasswordRoute = 'changePassword';
    private readonly organizationProfileRoute = 'organizationProfile';
    private readonly loginRoute = 'sessions/checkin';
    private readonly users = 'users';
    private readonly authSlug = 'auth';
    private readonly refreshTokenRoute = 'sessions/token';
    private readonly resetRoute = 'account/reset';
    private readonly route = '/';
    private readonly apiGatewayUrl: string;
    private readonly confirmationPendingSlug = 'account/confirmation-pending';
    private readonly resetPasswordSlug = 'password-reset';

    private readonly userProfileRoute = 'userProfile';
    private readonly organizationsRoute = 'organizations';
    private readonly operationsRoute = 'operations';
    private readonly licenceDataRoute = 'licence-data';
    private readonly invitations = 'invitations';
    private readonly emailVerificationSlug = 'email-verification';
    private readonly emailConfirmationSlug = 'email-confirmation';
    private readonly current = 'current';
    private readonly csrfSlug = 'csrf-token';
    private readonly blockageSlug = 'blockage';
    private readonly continuationSlug = 'continuation';
    private readonly adminDemotionSlug = 'admin-demotion';
    private readonly adminPromotionSlug = 'admin-promotion';
    private readonly acceptanceSlug = 'acceptance';
    private readonly stateSlug = 'state';
    private readonly tierSlug = 'tier';
    private readonly subscriptionTierSlug = 'subscription';
    private readonly localeSlug = 'locale';
    private readonly abcpSlug = 'abcp';
    private readonly settingsSlug = 'settings';
    private readonly reportingTemplate = 'reporting-template';
    private readonly reportingTemplateHgb = 'reporting-template-hgb';
    private readonly excelTemplate = 'excel-template';
    private readonly excelTemplateHgb = 'excel-template-hgb';
    private readonly referrals = 'referrals';
    private readonly agreeToTerms = 'agree-to-terms';
    private readonly updatePreferences = 'update-preferences';
    private readonly defaultTemplate = 'default-template';
    private readonly defaultHgbTemplate = 'default-hgb-template';
    private readonly defaultExcelTemplate = 'default-excel-template';
    private readonly defaultHgbExcelTemplate = 'default-hgb-excel-template';
    private readonly tasks = 'tasks';
    private readonly workflows = 'workflow';
    private readonly workflowSections = 'workflow-sections';
    private readonly locales = 'locales';
    private readonly configurations = 'configurations';
    private readonly resend = 'resend';
    private readonly projects = 'projects';
    private readonly withdarw = 'withdraw';
    private readonly sso = 'auth/sso';

    constructor(@Inject(UsersConfigService) usersConfig: UsersConfig) {
        this.apiGatewayUrl = usersConfig.apiGateway;
    }

    public getAllUsersForAdmin(): string {
        return `${this.apiGatewayUrl}${this.users}`;
    }

    public getChangePasswordRoute(): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}${this.changePasswordRoute}`;
    }

    public getOrganizationProfileRoute(): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}${this.organizationProfileRoute}`;
    }

    public getLoginRoute(): string {
        return `${this.apiGatewayUrl}${this.loginRoute}`;
    }

    public getRefreshTokenRoute(): string {
        return `${this.apiGatewayUrl}${this.refreshTokenRoute}`;
    }

    public getResetRoute(): string {
        return `${this.apiGatewayUrl}${this.resetRoute}`;
    }

    public getConfirmationPending(): string {
        return `${this.apiGatewayUrl}${this.confirmationPendingSlug}`;
    }

    public getUserProfileRoute(): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}${this.userProfileRoute}`;
    }

    public getResetPasswordRoute(): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}${this.resetPasswordSlug}`;
    }

    public getUsersUrl(): string {
        return `${this.apiGatewayUrl}${this.users}`;
    }
    public getResendVerificationMailUrl(): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}${this.emailVerificationSlug}`;
    }

    public confirmUserSigningUpUrl(): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}${this.current}${this.route}${this.emailConfirmationSlug}`;
    }

    public getCurrentUserUrl(): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}${this.current}`;
    }

    public getCsrfToken(): string {
        return `${this.apiGatewayUrl}${this.authSlug}${this.route}${this.csrfSlug}`;
    }
    public getLocalizationRoute(): string {
        return `${this.apiGatewayUrl}localization/`;
    }

    public getInvitationsUrl(): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${this.current}${this.route}${this.invitations}`;
    }

    public getReferralsInviteUrl(): string {
        return `${this.apiGatewayUrl}${this.referrals}`;
    }
    public getReferralsAcceptUrl(uuid: string): string {
        return `${this.apiGatewayUrl}${this.referrals}${this.route}${uuid}${this.route}${this.acceptanceSlug}`;
    }
    public getInvitationByUuid(uuid: string): string {
        return `${this.apiGatewayUrl}${this.invitations}${this.route}${uuid}`;
    }
    public acceptInvitation(uuid: string): string {
        return `${this.apiGatewayUrl}${this.invitations}${this.route}${uuid}${this.route}${this.acceptanceSlug}`;
    }

    public getCurrentOrganizationUrl(): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${this.current}`;
    }
    public getAllOrganizationsUrl(): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}`;
    }
    public createSearchIndex(): string {
        return `${this.apiGatewayUrl}${this.operationsRoute}/index`;
    }
    public searchOrganizationsUrl(): string {
        return `${this.apiGatewayUrl}${this.operationsRoute}/search`;
    }
    public reactivateCompanyUrl(companyId: string): string {
        return `${this.apiGatewayUrl}${this.operationsRoute}/companies/${companyId}/reactivate`;
    }
    public getOrganizationCompaniesUrl(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.operationsRoute}/organizations/${organizationId}/companies`;
    }
    public fxBillingUrl(): string {
        return `${this.apiGatewayUrl}${this.operationsRoute}/fx-billing`;
    }
    public setFxBilledPeriodUrl(companyId: string): string {
        return `${this.apiGatewayUrl}${this.operationsRoute}/fx-billing/${companyId}`;
    }
    public updateOrganizationState(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${organizationId}${this.route}${this.stateSlug}`;
    }
    public updateOrganizationTier(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${organizationId}${this.route}${this.tierSlug}`;
    }
    public updateOrganizationSubscriptionTier(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${organizationId}${this.route}${this.subscriptionTierSlug}`;
    }

    public updateOrganizationAbcp(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${organizationId}${this.route}${this.abcpSlug}`;
    }

    public updateOrganizationLocale(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${organizationId}${this.route}${this.localeSlug}`;
    }

    public updateOrganizationSettings(): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${this.current}${this.route}${this.settingsSlug}`;
    }
    public getOrganizationSettings(): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${this.current}${this.route}${this.settingsSlug}`;
    }
    public getOrganizationUsersUrl(): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${this.current}${this.route}${this.users}`;
    }
    public updateUserRole(userId: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${this.current}${this.route}${this.users}${this.route}${userId}/change-role`;
    }

    public bulkUpdateUserRole(): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${this.current}${this.route}${this.users}/bulk/change-role`;
    }

    public updateUserRoleByAppAdmin(userId: string): string {
        return `${this.apiGatewayUrl}${this.operationsRoute}${this.route}${this.users}${this.route}${userId}/change-role`;
    }

    public blockUser(id: string): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}${id}${this.route}${this.blockageSlug}`;
    }
    public unblockUser(id: string): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}${id}${this.route}${this.continuationSlug}`;
    }

    public demoteUser(id: string): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}${id}${this.route}${this.adminDemotionSlug}`;
    }
    public promoteUser(id: string): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}${id}${this.route}${this.adminPromotionSlug}`;
    }

    public getUploadOrReportingTemplateUrl(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${organizationId}${this.route}${this.reportingTemplate}`;
    }

    public getUploadHgbReportingTemplateUrl(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${organizationId}${this.route}${this.reportingTemplateHgb}`;
    }

    public getAgreeToTermsUrl(): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}${this.current}${this.route}${this.agreeToTerms}`;
    }

    public getSupportedLocales(): string {
        return `${this.apiGatewayUrl}${this.configurations}${this.route}${this.locales}`;
    }

    public getUpdateUserPreferencesUrl(): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}${this.current}${this.route}${this.updatePreferences}`;
    }

    public getSetOrganizationReportingTemplateToDefaultUrl(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${organizationId}${this.route}${this.defaultTemplate}`;
    }

    public getSetOrganizationHgbReportingTemplateToDefaultUrl(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${organizationId}${this.route}${this.defaultHgbTemplate}`;
    }

    public getUploadOrExcelTemplateUrl(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${organizationId}${this.route}${this.excelTemplate}`;
    }

    public getUploadHgbExcelTemplateUrl(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${organizationId}${this.route}${this.excelTemplateHgb}`;
    }

    public getSetOrganizationExcelTemplateToDefaultUrl(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${organizationId}${this.route}${this.defaultExcelTemplate}`;
    }

    public getSetOrganizationHgbExcelTemplateToDefaultUrl(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${organizationId}${this.route}${this.defaultHgbExcelTemplate}`;
    }

    public getActivateFCPTrialUrl(): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}ask-for-fcp-approval`;
    }

    public getDeactivateFCPUrl(): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}deactivate-fcp`;
    }

    public getChangeFCPStateUrl(id: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${id}${this.route}change-fcp-status`;
    }

    public getChangeCPMMStateUrl(id: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${id}${this.route}change-cpmm-state`;
    }

    public createLicencePackageUrl(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.licenceDataRoute}${this.route}${organizationId}${this.route}packages`;
    }
    public updateLicencePackageUrl(organizationId: string, packageId: string): string {
        return `${this.apiGatewayUrl}${this.licenceDataRoute}${this.route}${organizationId}${this.route}packages${this.route}${packageId}`;
    }

    public deleteLicencePackageUrl(packageId: string, organizationId: string): string {
        return `${this.apiGatewayUrl}${this.licenceDataRoute}${this.route}${packageId}${this.route}organization${this.route}${organizationId}`;
    }
    public getLicenceStatusUrl(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.licenceDataRoute}${this.route}${organizationId}${this.route}status`;
    }
    public requestLicenceUpgradeUrl(organizationId: string): string {
        return `${this.apiGatewayUrl}${this.licenceDataRoute}${this.route}${organizationId}${this.route}upgrade`;
    }

    public withdrawInvitation(): string {
        return `${this.apiGatewayUrl}${this.invitations}${this.route}${this.withdarw}`;
    }

    public resendInvitation(uuid: string): string {
        return `${this.apiGatewayUrl}${this.invitations}${this.route}${uuid}${this.route}${this.resend}`;
    }

    public getUserById(userId: string): string {
        return `${this.apiGatewayUrl}${this.organizationsRoute}${this.route}${this.users}${this.route}${userId}`;
    }

    public getUserProjectsTasks(): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}me${this.route}${this.tasks}`;
    }

    /** REV-22155 — `personProject`: `/person-projects/:id/tasks`. */
    public getProjectsTasks(projectId: string, personProject = false): string {
        return `${this.apiGatewayUrl}${personProject ? 'person-projects' : this.projects}/${projectId}/tasks`;
    }

    public createTask(projectId: string, personProject = false): string {
        return `${this.apiGatewayUrl}${personProject ? 'person-projects' : this.projects}/${projectId}/tasks`;
    }

    public updateTask(taskId: string): string {
        return `${this.apiGatewayUrl}${this.tasks}${this.route}${taskId}`;
    }

    public bulkUpdateTasks(): string {
        return `${this.apiGatewayUrl}${this.tasks}/bulk-update`;
    }

    public bulkDeleteTasks(): string {
        return `${this.apiGatewayUrl}${this.tasks}/bulk-delete`;
    }

    public updateAllTasksStatusForProjectBySectionId(projectId: string, sectionId: string): string {
        return `${this.apiGatewayUrl}${this.tasks}/project/${projectId}/section/${sectionId}`;
    }

    // ── MFA / 2FA routes ────────────────────────────────────────────────────────
    public getMfaCheckUrl(): string {
        return `${this.apiGatewayUrl}auth/2fa/check`;
    }
    public getMfaSetupInitiateUrl(): string {
        return `${this.apiGatewayUrl}auth/2fa/setup/initiate`;
    }
    public getMfaSetupVerifyUrl(): string {
        return `${this.apiGatewayUrl}auth/2fa/setup/verify`;
    }
    public getMfaVerifyUrl(): string {
        return `${this.apiGatewayUrl}auth/2fa/verify`;
    }
    public getMfaOrgStatusUrl(): string {
        return `${this.apiGatewayUrl}organizations/current/2fa/status`;
    }
    public getMfaOrgEnableUrl(): string {
        return `${this.apiGatewayUrl}organizations/current/2fa/enable`;
    }
    public getMfaOrgDisableUrl(): string {
        return `${this.apiGatewayUrl}organizations/current/2fa/disable`;
    }
    public getMfaOrgUsersUrl(): string {
        return `${this.apiGatewayUrl}organizations/current/2fa/users`;
    }
    public getMfaResetUserUrl(userId: string): string {
        return `${this.apiGatewayUrl}organizations/current/2fa/users/${userId}/reset`;
    }

    public deleteTask(taskId: string): string {
        return `${this.apiGatewayUrl}${this.tasks}${this.route}${taskId}`;
    }

    public getSsoProvider(handle: string): string {
        return `${this.apiGatewayUrl}${this.sso}${this.route}${handle}`;
    }
    public startSsoUrl(handle: string): string {
        return `${this.apiGatewayUrl}${this.sso}${this.route}${handle}/start`;
    }
    public exchangeSsoCodeWithToken(handle: string): string {
        return `${this.apiGatewayUrl}${this.sso}${this.route}${handle}/callback/`;
    }
    public initSsoAccount(): string {
        return `${this.apiGatewayUrl}${this.sso}${this.route}init`;
    }

    public getWorkflows(): string {
        return `${this.apiGatewayUrl}${this.workflows}`;
    }

    public getWorkflowById(id: string): string {
        return `${this.apiGatewayUrl}${this.workflows}${this.route}${id}`;
    }

    public getWorkflowSections(): string {
        return `${this.apiGatewayUrl}${this.workflowSections}`;
    }

    public getWorkflowSectionById(id: string): string {
        return `${this.apiGatewayUrl}${this.workflowSections}${this.route}${id}`;
    }

    public deleteUser(id: string): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}${id}`;
    }

    public dismissBanner(): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}${this.current}/dismissed-banners`;
    }

    public updateLastSeenReleaseId(): string {
        return `${this.apiGatewayUrl}${this.users}${this.route}${this.current}/last-seen-release`;
    }

    buildHttpParams(filter?: TasksFilter): HttpParams {
        let params = new HttpParams();

        if (!filter) {
            return params;
        }

        Object.entries(filter).forEach(([key, value]) => {
            if (value === undefined || value === null) return;

            if (Array.isArray(value)) {
                value.forEach((item) => {
                    if (typeof item === 'object') {
                        params = params.append(key, JSON.stringify(item));
                        return;
                    }
                    params = params.append(key, item.toString());
                });
                // workaround: repeating the value if filter property has only 1 item to be sent as an array to the backend
                if (value.length === 1) {
                    const singleValue = value[0];
                    params = params.append(
                        key,
                        typeof singleValue === 'object' ? JSON.stringify(singleValue) : singleValue.toString(),
                    );
                }
            } else if (typeof value === 'object') {
                params = params.append(key, JSON.stringify(value));
            } else {
                params = params.append(key, value.toString());
            }
        });

        return params;
    }

    buildMfaHttpParams(filter?: MfaFilter): HttpParams {
        let params = new HttpParams();

        if (!filter) {
            return params;
        }

        if (filter.sortBy !== undefined) {
            params = params.set('sortBy', filter.sortBy.toString());
        }
        if (filter.sortOrder !== undefined) {
            params = params.set('sortOrder', filter.sortOrder.toString());
        }

        return params;
    }

}
